/**
 * Safe relative-path validation for post-auth redirect targets.
 *
 * We accept a redirect target ONLY when it is a same-origin, in-app path.
 * Absolute URLs (http://..., https://..., //...), protocol-relative URLs,
 * javascript: schemes, and paths that jump out of the app root are all
 * rejected \u2014 that would let a phisher craft a URL like
 * `https://creatorvault.co/auth?redirect=https://phish.example.com/steal`
 * that hijacks the session redirect after the user signs in.
 *
 * The allowlist:
 *   - Must be a non-empty string
 *   - Must start with `/`
 *   - Must NOT start with `//` (protocol-relative, points to another host)
 *   - Must NOT contain `\\` (Windows-style path traversal that some browsers
 *     normalize to `/` before parsing origin)
 *   - Must not contain a colon before the first `/` (guards against
 *     `javascript:` and `data:` payloads even though the leading-`/` check
 *     already blocks them)
 *   - Length capped at 512 chars to prevent DOS
 */
export function isSafeRelativePath(input: string | null | undefined): boolean {
  if (!input || typeof input !== "string") return false;
  if (input.length > 512) return false;
  // Must be an absolute path (starts with /) but NOT protocol-relative (//).
  if (!input.startsWith("/")) return false;
  if (input.startsWith("//")) return false;
  // Reject Windows-style backslashes anywhere in the path \u2014 some browsers
  // normalize these to `/` post-parse, which can let attackers smuggle a
  // different origin past the `startsWith('/')` check.
  if (input.includes("\\")) return false;
  // Reject anything that could be interpreted as a scheme (safety net; the
  // leading `/` check above already blocks `javascript:` and `data:`).
  const beforeFirstSlash = input.split("/")[0];
  if (beforeFirstSlash.includes(":")) return false;
  return true;
}
